import type { ReactNode } from "react";

/**
 * The four doors in — one shell, six contents.
 *
 * 🔴 THE BUILD SHIPPED ONE SIGN-IN SCREEN FOR EVERY ENTRANCE. `Apply` — *"Sign in with Google
 * to apply to a cohort, or to see an application you have already sent"* — is correct for
 * exactly one URL and was shown on all of them: the console's own front door, where the copy
 * is a different principal's, and the link a teacher sends to somebody who is ALREADY ENROLLED
 * and only needs to answer the project form. The route knows which door it is. Nothing here is
 * a judgement at runtime; each screen is decided by the URL and, on the refusal, by one server
 * field.
 *
 * 🔴 EVERY SENTENCE IS THE EXISTING COPY, VERBATIM. The handoff is explicit that no sign-in
 * wording is rewritten — the words came from `Apply.tsx` and `App.tsx` and stay there. What
 * changes is the shell around them and which of them a given route gets.
 *
 * ⚠️ THE TWO FINE-PRINT STRINGS ARE NOT SHARED, AND THAT IS THE POINT. The student's says
 * signing in asks only for a name and email; the teacher's adds that reading Classroom is done
 * by the bot credential, separately. One string for both surfaces would tell a student about a
 * credential that has nothing to do with them, or drop the clause that matters to a teacher.
 */
export function Panel({ scan = true, sent = false, children }: {
  /** The 135° scanline. Omitted on refusals — "this is a refusal, not a door". */
  scan?: boolean;
  /** The stronger border, for the panel a teacher is actively sending people to. */
  sent?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`panel${sent ? " sent" : ""}`}>
      {/* 🔴 NO WORDMARK IN THE PANEL, AGAINST THE PROTOTYPE — AND THE RENDER IS WHY. The
          prototype is a STANDALONE screen with no shell, so it carries its own logo on its own
          dark ground. Both surfaces here already have one: `ApplyShell`'s brand band and
          `Plain`'s topbar, both dark in both themes because the supplied wordmark is white
          with a #00FF99 "3" and ships no dark-on-light variant.
          [X] Putting it in the panel too gave every sign-in screen TWO wordmarks stacked — and
          in light mode the lower one VANISHED, white on white, leaving a stray green "3"
          floating above the heading. Caught by rendering both themes at 1600px, not by
          reading. The same reasoning was already recorded for the refusal panel. */}
      <div className={scan ? "panel-scan" : ""}>
        <div className="panel-body">{children}</div>
      </div>
    </div>
  );
}

/** The route, the round, or the principal — whichever names where the reader is. */
export function Eyebrow({ tone, tag, children }: {
  tone?: "warn"; tag?: string; children: ReactNode;
}) {
  return (
    <p className={`panel-eyebrow${tone === "warn" ? " warn" : ""}`}>
      <span>{children}</span>
      {tag && <span className="rt">{tag}</span>}
    </p>
  );
}

/**
 * The primary action. An `<a>`, never a button — sign-in is a navigation to Google, and the
 * two start URLs are not interchangeable: `SIGN_IN_URL` is the console's and
 * `APPLY_SIGN_IN_URL(courseId)` is a student's. Two principals, two surfaces.
 */
export function PanelPrimary({ href, children }: { href: string; children: ReactNode }) {
  return <a className="panel-primary" href={href}>{children}</a>;
}
